if (!customElements.get("upsell-popup")) {
  customElements.define(
    "upsell-popup",
    class UpsellPopup extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector(".upsell-popup__dialog");
        this.content = this.querySelector("[data-upsell-popup-content]");
        this.cart = document.querySelector("cart-drawer");
        this.isDrawer = this.cart?.dataset.cartType === "drawer";

        // Closing the pop-up continues the regular flow: the cart drawer opens
        this.querySelectorAll("[data-upsell-popup-close]").forEach((el) =>
          el.addEventListener("click", (e) => {
            if (this.isDrawer) e.preventDefault();
            this.close(true);
          }),
        );
        this.querySelector("[data-upsell-popup-continue]")?.addEventListener(
          "click",
          () => this.close(false),
        );
        this.content.addEventListener("click", (e) => {
          const button = e.target.closest("[data-upsell-add]");
          if (button) this.addItem(button.closest("[data-upsell-item]"), button);
        });
        this.addEventListener("keydown", this.onKeydown.bind(this));
      }

      // Resolves to true when the pop-up was shown, false when the product has no upsells
      openFor(lineItem) {
        if (!lineItem?.handle) return Promise.resolve(false);

        const url = `${window.Shopify.routes.root}products/${lineItem.handle}?section_id=upsell-popup`;
        return fetch(url)
          .then((response) => (response.ok ? response.text() : ""))
          .then((html) => {
            const items = new DOMParser()
              .parseFromString(html, "text/html")
              .querySelector("[data-upsell-popup-items]");
            if (!items) return false;

            this.content.replaceChildren(items);
            this.updateAddedProduct(lineItem);
            this.open();
            return true;
          })
          .catch((e) => {
            console.error(e);
            return false;
          });
      }

      open() {
        this.isOpen = true;
        this.lastFocus = document.activeElement;
        this.hidden = false;
        document.body.classList.add("upsell-popup-open");
        // Force a reflow so the transition runs; requestAnimationFrame is paused in background tabs
        void this.offsetWidth;
        this.classList.add("upsell-popup--open");
        this.dialog.focus();
      }

      close(openCart = true) {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove("upsell-popup--open");
        document.body.classList.remove("upsell-popup-open");
        setTimeout(() => {
          if (!this.isOpen) this.hidden = true;
        }, 250);

        if (openCart && this.isDrawer) {
          if (!this.cart.isOpen) this.cart.toggleDrawerClasses();
        } else {
          this.lastFocus?.focus();
        }
      }

      onKeydown(e) {
        if (e.key === "Escape") {
          this.close(true);
          return;
        }
        if (e.key !== "Tab") return;

        const focusable = Array.from(
          this.dialog.querySelectorAll("button, [href], input, select"),
        ).filter(
          (el) =>
            !el.disabled &&
            el.type !== "hidden" &&
            el.tabIndex >= 0 &&
            el.offsetParent !== null,
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }

      updateAddedProduct(lineItem) {
        const variantInfo = this.content.querySelector(
          "[data-upsell-popup-variant]",
        );
        if (!variantInfo) return;
        const parts = [];
        if (!lineItem.product_has_only_default_variant && lineItem.variant_title)
          parts.push(lineItem.variant_title);
        if (lineItem.quantity) parts.push(`${lineItem.quantity}×`);
        variantInfo.textContent = parts.join(" · ");
      }

      // Re-renders the cart drawer; falls back to fetching its sections when the
      // add-to-cart response has none
      renderCart(response) {
        if (!this.isDrawer) return;
        const hasSections = this.cart
          .getSectionsToRender()
          .every((section) => response?.sections?.[section.id]);
        if (hasSections) this.cart.renderContents(response, false);
        else this.cart.refreshCartDrawer();
      }

      setLoading(button, isLoading) {
        button.classList.toggle("loading", isLoading);
        button
          .querySelector(".loading-overlay__spinner")
          ?.classList.toggle("hidden", !isLoading);
        if (isLoading) button.setAttribute("aria-disabled", "true");
        else button.removeAttribute("aria-disabled");
      }

      addItem(item, button) {
        if (button.getAttribute("aria-disabled") === "true") return;

        const id = item.querySelector("[data-upsell-variant]").value;
        const quantity =
          parseInt(item.querySelector(".js-counter-quantity")?.value) || 1;
        const error = item.querySelector("[data-upsell-error]");

        error.hidden = true;
        this.setLoading(button, true);

        // Same request shape as product-form.js (FormData): cart apps that intercept
        // /cart/add (e.g. Restock Rocket) handle it, and the response keeps "sections"
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        const formData = new FormData();
        formData.append("id", id);
        formData.append("quantity", quantity);
        if (this.isDrawer) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((s) => s.id),
          );
          formData.append("sections_url", window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.setLoading(button, false);
            if (response.status) {
              error.textContent = response.description || response.message;
              error.hidden = false;
              return;
            }

            // Publish before re-rendering, like product-form.js: CartItems re-fetches the
            // cart page section on cartUpdate, so only the replaced drawer may receive it
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "upsell-popup",
              productVariantId: id,
              cartData: response,
            });
            this.renderCart(response);

            button.querySelector("span").textContent = button.dataset.addedText;
            button.setAttribute("aria-disabled", "true");
            item.classList.add("upsell-popup__item--added");
          })
          .catch((e) => {
            console.error(e);
            this.setLoading(button, false);
          });
      }
    },
  );
}
